import React, { useMemo, useRef, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { uploadImage } from '../utils/cloudflare';
import { mediaThumb } from './MediaGallery';
import { PlatformOptions } from './CampaignForm';
import ImageCropModal from './ImageCropModal';
import { RateCardEditor } from './RateCard';
import { mediaKitUrl } from '../utils/siteUrl';

const uid = () => Math.random().toString(36).slice(2, 10);

const AGE_BANDS = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];

// The press page frames the cover with `object-cover` across the full width of
// the screen, so an uncropped upload arrived as a random middle band of itself
// and the creator only found out from the finished page. 3:1 is the band that
// survives both the phone header and the desktop one.
export const MEDIA_KIT_COVER_ASPECT = 3;

function Fieldset({ title, hint, children }) {
    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            {hint && <p className="text-xs text-slate-400 mt-0.5 mb-3 leading-relaxed">{hint}</p>}
            <div className={hint ? '' : 'mt-3'}>{children}</div>
        </section>
    );
}

/**
 * A label/percent list — the shape all three audience splits share.
 * The running total is shown next to the heading rather than enforced: an
 * analytics screen rarely adds to exactly 100, and rejecting 97% would just
 * make the creator invent 3.
 */
function SplitRows({ rows, presets, placeholder, onChange, t }) {
    const total = rows.reduce((sum, row) => sum + (Number(row.percent) || 0), 0);

    const update = (id, key, value) => onChange(
        rows.map(row => (row.id === id ? { ...row, [key]: value } : row)));

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400">{t('mediaKit.splitTotal')}</span>
                <span className={`text-[11px] font-bold tabular-nums ${total > 100 ? 'text-red-500' : 'text-slate-500'}`}>
                    {total}%
                </span>
            </div>

            {rows.map(row => (
                <div key={row.id} className="flex items-center gap-2">
                    {presets ? (
                        <select className="input py-1.5 text-sm flex-1" value={row.label || ''}
                            onChange={(e) => update(row.id, 'label', e.target.value)}>
                            <option value="">{placeholder}</option>
                            {presets.map(preset => <option key={preset} value={preset}>{preset}</option>)}
                        </select>
                    ) : (
                        <input className="input py-1.5 text-sm flex-1" value={row.label || ''} maxLength={60}
                            placeholder={placeholder}
                            onChange={(e) => update(row.id, 'label', e.target.value)} />
                    )}
                    <div className="relative w-20">
                        <input type="number" min={0} max={100} className="input py-1.5 pr-6 text-sm w-full"
                            value={row.percent ?? ''}
                            onChange={(e) => update(row.id, 'percent', e.target.value)} />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
                    </div>
                    <button type="button" onClick={() => onChange(rows.filter(r => r.id !== row.id))}
                        className="text-red-300 hover:text-red-600 px-1">×</button>
                </div>
            ))}

            <button type="button" onClick={() => onChange([...rows, { id: uid(), label: '', percent: '' }])}
                className="btn-secondary text-xs py-1.5 px-3">+ {t('mediaKit.addRow')}</button>
        </div>
    );
}

/**
 * The creator's media kit editor — a full-screen sheet, same pattern as the
 * campaign form.
 *
 * `data` is the payload from GET /api/media-kit/: {kit, metrics, rate_card,
 * platform_deals, gallery}. Everything typed in here is self-reported and the
 * sheet says so, because the published page says so too.
 */
export default function MediaKitEditor({ data, onClose, onSaved }) {
    const { t } = useTranslation();
    const kit = data?.kit;

    const withIds = (rows) => (rows || []).map(row => ({ id: uid(), ...row }));

    const [form, setForm] = useState(() => ({
        headline: kit?.headline || '',
        bio: kit?.bio || '',
        location: kit?.location || '',
        contact_email: kit?.contact_email || '',
        cover_image: kit?.cover_image || '',
        languages: kit?.languages || [],
        audience_countries: withIds(kit?.audience_countries),
        audience_age: withIds(kit?.audience_age),
        audience_gender: withIds(kit?.audience_gender),
        highlights: withIds(kit?.highlights),
        past_collaborations: withIds(kit?.past_collaborations),
        sample_content: withIds(kit?.sample_content),
        is_public: kit?.is_public ?? false,
        show_platform_deals: kit?.show_platform_deals ?? true,
    }));

    const [socials, setSocials] = useState(
        () => (data?.metrics?.platforms || []).map(row => ({ ...row })));
    const [shareToken, setShareToken] = useState(kit?.share_token || '');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [langInput, setLangInput] = useState('');
    const [coverFile, setCoverFile] = useState(null);   // waiting to be cropped
    const saveRates = useRef(null);   // filled in by the nested rate card editor

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // The same weighting the backend applies, mirrored here so the creator
    // sees what a brand will see while they are still typing.
    const preview = useMemo(() => {
        const followers = socials.reduce((sum, s) => sum + (Number(s.followers) || 0), 0);
        const reach = socials.reduce((sum, s) => sum + (Number(s.avg_reach) || 0), 0);
        const weighted = socials.reduce(
            (sum, s) => sum + (Number(s.engagement_rate) || 0) * (Number(s.followers) || 0), 0);
        return {
            followers, reach,
            engagement: followers ? Math.round((weighted / followers) * 100) / 100 : 0,
        };
    }, [socials]);

    const updateSocial = (id, key, value) => setSocials(prev =>
        prev.map(row => (row.id === id ? { ...row, [key]: value } : row)));

    // Picking a file opens the crop sheet rather than uploading straight away —
    // the frame the creator chooses here is the frame the page publishes.
    const pickCover = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';        // so re-picking the same file re-fires
        if (file) setCoverFile(file);
    };

    const uploadCover = async (cropped) => {
        setUploading(true);
        try {
            const url = await uploadImage(cropped);
            if (url) set('cover_image', url);
            setCoverFile(null);
        } catch (err) {
            setError(t('campaigns.uploadError'));
            setCoverFile(null);
        } finally {
            setUploading(false);
        }
    };

    const toggleSample = (item) => {
        const existing = form.sample_content.find(row => row.url === item.url);
        set('sample_content', existing
            ? form.sample_content.filter(row => row.url !== item.url)
            : [...form.sample_content, { id: uid(), type: item.type, url: item.url, note: '' }]);
    };

    const addLanguage = () => {
        const value = langInput.trim();
        if (!value || form.languages.includes(value)) return;
        set('languages', [...form.languages, value]);
        setLangInput('');
    };

    // Drop rows the creator added but never filled in, and send the numeric
    // fields as numbers — they are read back as numbers by the bars and the
    // applicant block, so they must not go out as "68".
    const NUMERIC = ['percent', 'views', 'year'];
    const strip = (rows) => rows
        .filter(row => Object.entries(row).some(
            ([key, value]) => key !== 'id' && value !== '' && value !== null && value !== undefined))
        .map(row => {
            const clean = { ...row };
            NUMERIC.forEach(key => {
                if (clean[key] !== undefined && clean[key] !== '') clean[key] = Number(clean[key]) || 0;
                else delete clean[key];
            });
            return clean;
        });

    const save = async (publish) => {
        setSaving(true);
        setError('');
        try {
            // Prices are edited in the same sheet, so the same button saves
            // them — otherwise the footer button would silently discard them.
            if (saveRates.current) await saveRates.current();

            const res = await api.put('/api/media-kit/', {
                ...form,
                is_public: publish ?? form.is_public,
                audience_countries: strip(form.audience_countries),
                audience_age: strip(form.audience_age),
                audience_gender: strip(form.audience_gender),
                highlights: strip(form.highlights),
                past_collaborations: strip(form.past_collaborations),
                sample_content: strip(form.sample_content),
                socials: socials.map(row => ({
                    id: row.id,
                    avg_reach: Number(row.avg_reach) || 0,
                    avg_views: Number(row.avg_views) || 0,
                    engagement_rate: Number(row.engagement_rate) || 0,
                })),
            });
            if (res.status === 200) {
                if (publish !== undefined) set('is_public', publish);
                onSaved?.(res.data);
                onClose();
            }
        } catch (err) {
            setError(err.response?.data?.error === 'creators_only'
                ? t('mediaKit.creatorsOnly')
                : t('campaigns.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const rotateToken = async () => {
        try {
            const res = await api.post('/api/media-kit/', { action: 'rotate_token' });
            if (res.status === 200) { setShareToken(res.data.share_token); setCopied(false); }
        } catch (err) { /* surfaced by the interceptor */ }
    };

    const privateLink = shareToken
        ? mediaKitUrl(data?.username || '', shareToken)
        : '';

    const copyLink = () => {
        navigator.clipboard.writeText(privateLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="fixed inset-0 z-[9000] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-slate-50 w-full sm:max-w-3xl h-[92vh] sm:h-[88vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">

                <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-100 flex-shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-lg font-black text-slate-800">{t('mediaKit.editTitle')}</h2>
                        <p className="text-xs text-slate-400 truncate">{t('mediaKit.editHint')}</p>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-5 py-4 space-y-4">

                    {/* ── Who you are ── */}
                    <Fieldset title={t('mediaKit.identityTitle')} hint={t('mediaKit.identityHint')}>
                        <label className="label">{t('mediaKit.fieldHeadline')}</label>
                        <input className="input" value={form.headline} maxLength={140}
                            placeholder={t('mediaKit.headlinePlaceholder')}
                            onChange={(e) => set('headline', e.target.value)} />

                        <label className="label mt-3">{t('mediaKit.fieldBio')}</label>
                        <textarea className="input min-h-[90px] resize-y" value={form.bio} maxLength={1500}
                            placeholder={t('mediaKit.bioPlaceholder')}
                            onChange={(e) => set('bio', e.target.value)} />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="label">{t('mediaKit.fieldLocation')}</label>
                                <input className="input" value={form.location} maxLength={120}
                                    placeholder={t('mediaKit.locationPlaceholder')}
                                    onChange={(e) => set('location', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('mediaKit.fieldContactEmail')}</label>
                                <input className="input" type="email" value={form.contact_email} maxLength={254}
                                    placeholder="isim@ajans.com"
                                    onChange={(e) => set('contact_email', e.target.value)} />
                            </div>
                        </div>

                        <label className="label mt-3">{t('mediaKit.fieldLanguages')}</label>
                        <div className="flex gap-2">
                            <input className="input" value={langInput} maxLength={40}
                                placeholder={t('mediaKit.languagePlaceholder')}
                                onChange={(e) => setLangInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }} />
                            <button type="button" onClick={addLanguage} className="btn-secondary px-3 text-sm">+</button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {form.languages.map(lang => (
                                <span key={lang} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold flex items-center gap-1">
                                    {lang}
                                    <button type="button" onClick={() => set('languages', form.languages.filter(x => x !== lang))}
                                        className="text-slate-400 hover:text-red-500">×</button>
                                </span>
                            ))}
                        </div>

                        <label className="label mt-4">{t('mediaKit.fieldCover')}</label>
                        {/* The preview is the published shape, so what is in
                            this box is what a brand sees at the top of the
                            page — no second surprise crop on the way out. */}
                        <div className="flex items-center gap-3">
                            {form.cover_image ? (
                                <>
                                    <div className="relative w-36 rounded-xl overflow-hidden border border-slate-200"
                                        style={{ aspectRatio: String(MEDIA_KIT_COVER_ASPECT) }}>
                                        <img src={form.cover_image} alt="" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => set('cover_image', '')}
                                            title={t('mediaKit.removeCover')}
                                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center">×</button>
                                    </div>
                                    <label className="btn-secondary text-xs py-1.5 px-3 cursor-pointer flex items-center gap-1.5">
                                        {uploading && <span className="w-3 h-3 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" />}
                                        {t('mediaKit.replaceCover')}
                                        <input type="file" accept="image/*" className="hidden" onChange={pickCover} />
                                    </label>
                                </>
                            ) : (
                                <label className="w-36 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary-400 transition text-slate-400"
                                    style={{ aspectRatio: String(MEDIA_KIT_COVER_ASPECT) }}>
                                    {uploading
                                        ? <span className="w-4 h-4 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" />
                                        : <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>}
                                    <input type="file" accept="image/*" className="hidden" onChange={pickCover} />
                                </label>
                            )}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{t('mediaKit.coverHint')}</p>
                    </Fieldset>

                    {/* ── Performance ── */}
                    <Fieldset title={t('mediaKit.performanceTitle')} hint={t('mediaKit.performanceHint')}>
                        {socials.length === 0 ? (
                            <p className="text-xs text-slate-400 italic py-2">{t('mediaKit.noSocials')}</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-center">
                                        <p className="text-base font-black text-slate-800 tabular-nums">
                                            {preview.followers.toLocaleString('en-US')}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-semibold">{t('profile.followers')}</p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-center">
                                        <p className="text-base font-black text-slate-800 tabular-nums">
                                            {preview.reach.toLocaleString('en-US')}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-semibold">{t('mediaKit.reach')}</p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-center">
                                        <p className="text-base font-black text-slate-800 tabular-nums">%{preview.engagement}</p>
                                        <p className="text-[10px] text-slate-500 font-semibold">{t('mediaKit.engagement')}</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {socials.map(social => (
                                        <div key={social.id} className="rounded-xl border border-slate-200 p-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-sm font-bold text-slate-700 capitalize">{social.social_media}</span>
                                                <span className="text-[11px] text-slate-400">@{social.username}</span>
                                                <span className="text-[11px] text-slate-500 font-semibold ml-auto tabular-nums">
                                                    {Number(social.followers || 0).toLocaleString('en-US')} {t('profile.followers')}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                <label className="block">
                                                    <span className="text-[10px] font-semibold text-slate-500">{t('mediaKit.avgReach')}</span>
                                                    <input type="number" min={0} className="input py-1.5 text-sm"
                                                        value={social.avg_reach ?? 0}
                                                        onChange={(e) => updateSocial(social.id, 'avg_reach', e.target.value)} />
                                                </label>
                                                <label className="block">
                                                    <span className="text-[10px] font-semibold text-slate-500">{t('mediaKit.avgViews')}</span>
                                                    <input type="number" min={0} className="input py-1.5 text-sm"
                                                        value={social.avg_views ?? 0}
                                                        onChange={(e) => updateSocial(social.id, 'avg_views', e.target.value)} />
                                                </label>
                                                <label className="block">
                                                    <span className="text-[10px] font-semibold text-slate-500">{t('mediaKit.engagementRate')}</span>
                                                    <input type="number" min={0} max={100} step="0.1" className="input py-1.5 text-sm"
                                                        value={social.engagement_rate ?? 0}
                                                        onChange={(e) => updateSocial(social.id, 'engagement_rate', e.target.value)} />
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 leading-relaxed">
                                    {t('mediaKit.selfReportedWarning')}
                                </p>
                            </>
                        )}
                    </Fieldset>

                    {/* ── Audience ── */}
                    <Fieldset title={t('mediaKit.audienceTitle')} hint={t('mediaKit.audienceHint')}>
                        <p className="label">{t('mediaKit.audienceCountries')}</p>
                        <SplitRows rows={form.audience_countries} placeholder={t('mediaKit.countryPlaceholder')}
                            onChange={(rows) => set('audience_countries', rows)} t={t} />

                        <p className="label mt-4">{t('mediaKit.audienceAge')}</p>
                        <SplitRows rows={form.audience_age} presets={AGE_BANDS}
                            placeholder={t('mediaKit.agePlaceholder')}
                            onChange={(rows) => set('audience_age', rows)} t={t} />

                        <p className="label mt-4">{t('mediaKit.audienceGender')}</p>
                        <SplitRows rows={form.audience_gender} placeholder={t('mediaKit.genderPlaceholder')}
                            onChange={(rows) => set('audience_gender', rows)} t={t} />
                    </Fieldset>

                    {/* ── Highlights ── */}
                    <Fieldset title={t('mediaKit.highlightsTitle')} hint={t('mediaKit.highlightsHint')}>
                        <div className="space-y-2">
                            {form.highlights.map(row => (
                                <div key={row.id} className="flex items-center gap-2">
                                    <input className="input py-1.5 text-sm flex-1" value={row.label || ''} maxLength={60}
                                        placeholder={t('mediaKit.highlightLabelPlaceholder')}
                                        onChange={(e) => set('highlights', form.highlights.map(
                                            r => (r.id === row.id ? { ...r, label: e.target.value } : r)))} />
                                    <input className="input py-1.5 text-sm w-28" value={row.value || ''} maxLength={40}
                                        placeholder={t('mediaKit.highlightValuePlaceholder')}
                                        onChange={(e) => set('highlights', form.highlights.map(
                                            r => (r.id === row.id ? { ...r, value: e.target.value } : r)))} />
                                    <button type="button" onClick={() => set('highlights', form.highlights.filter(r => r.id !== row.id))}
                                        className="text-red-300 hover:text-red-600 px-1">×</button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={() => set('highlights', [...form.highlights, { id: uid(), label: '', value: '' }])}
                            className="btn-secondary text-xs mt-3 py-1.5 px-3">+ {t('mediaKit.addRow')}</button>
                    </Fieldset>

                    {/* ── Past collaborations ── */}
                    <Fieldset title={t('mediaKit.collabsTitle')} hint={t('mediaKit.collabsHint')}>
                        {data?.platform_deals?.count > 0 && (
                            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-3">
                                <div className="flex items-start gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-emerald-800">
                                            {data.platform_deals.count} {t('mediaKit.verifiedCollabs')}
                                        </p>
                                        <p className="text-[11px] text-emerald-700 mt-0.5 leading-relaxed">
                                            {t('mediaKit.verifiedCollabsDesc')}
                                        </p>
                                    </div>
                                </div>
                                <label className="flex items-center justify-between gap-3 mt-2.5 cursor-pointer">
                                    <span className="text-[11px] font-semibold text-emerald-800">{t('mediaKit.showPlatformDeals')}</span>
                                    <input type="checkbox" checked={form.show_platform_deals}
                                        onChange={(e) => set('show_platform_deals', e.target.checked)}
                                        className="w-4 h-4 accent-[#20c9b5]" />
                                </label>
                            </div>
                        )}

                        <div className="space-y-2">
                            {form.past_collaborations.map(row => (
                                <div key={row.id} className="rounded-xl border border-slate-200 p-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input className="input py-1.5 text-sm flex-1" value={row.brand || ''} maxLength={80}
                                            placeholder={t('mediaKit.collabBrandPlaceholder')}
                                            onChange={(e) => set('past_collaborations', form.past_collaborations.map(
                                                r => (r.id === row.id ? { ...r, brand: e.target.value } : r)))} />
                                        <input type="number" className="input py-1.5 text-sm w-20" value={row.year || ''}
                                            placeholder={t('mediaKit.collabYearPlaceholder')}
                                            onChange={(e) => set('past_collaborations', form.past_collaborations.map(
                                                r => (r.id === row.id ? { ...r, year: e.target.value } : r)))} />
                                        <button type="button" onClick={() => set('past_collaborations', form.past_collaborations.filter(r => r.id !== row.id))}
                                            className="text-red-300 hover:text-red-600 px-1">×</button>
                                    </div>
                                    <input className="input py-1.5 text-sm" value={row.note || ''} maxLength={140}
                                        placeholder={t('mediaKit.collabNotePlaceholder')}
                                        onChange={(e) => set('past_collaborations', form.past_collaborations.map(
                                            r => (r.id === row.id ? { ...r, note: e.target.value } : r)))} />
                                    <input className="input py-1.5 text-sm" value={row.link || ''}
                                        placeholder="https://..."
                                        onChange={(e) => set('past_collaborations', form.past_collaborations.map(
                                            r => (r.id === row.id ? { ...r, link: e.target.value } : r)))} />
                                </div>
                            ))}
                        </div>
                        <button type="button"
                            onClick={() => set('past_collaborations', [...form.past_collaborations, { id: uid(), brand: '', note: '', year: '', link: '' }])}
                            className="btn-secondary text-xs mt-3 py-1.5 px-3">+ {t('mediaKit.addCollab')}</button>
                    </Fieldset>

                    {/* ── Sample content ── */}
                    <Fieldset title={t('mediaKit.samplesTitle')} hint={t('mediaKit.samplesHint')}>
                        {data?.gallery?.length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                {data.gallery.map(item => {
                                    const picked = form.sample_content.some(row => row.url === item.url);
                                    return (
                                        <button key={item.id} type="button" onClick={() => toggleSample(item)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${picked
                                                ? 'border-primary-500 ring-2 ring-primary-200'
                                                : 'border-slate-200 hover:border-slate-300'}`}>
                                            <img
                                                src={mediaThumb(item, { height: 240 })}
                                                alt="" className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                            {picked && (
                                                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary-600 text-white text-[9px] flex items-center justify-center font-black">✓</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 italic">{t('mediaKit.noGallery')}</p>
                        )}

                        {/* Links to work that lives on the platforms themselves. */}
                        <div className="space-y-2 mt-3">
                            {form.sample_content.filter(row => !data?.gallery?.some(g => g.url === row.url)).map(row => (
                                <div key={row.id} className="flex items-center gap-2">
                                    <select className="input py-1.5 text-xs w-auto" value={row.platform || ''}
                                        onChange={(e) => set('sample_content', form.sample_content.map(
                                            r => (r.id === row.id ? { ...r, platform: e.target.value } : r)))}>
                                        <option value="">{t('campaigns.platform')}</option>
                                        <PlatformOptions t={t} />
                                    </select>
                                    <input className="input py-1.5 text-sm flex-1" value={row.url || ''}
                                        placeholder="https://..."
                                        onChange={(e) => set('sample_content', form.sample_content.map(
                                            r => (r.id === row.id ? { ...r, url: e.target.value } : r)))} />
                                    <input type="number" className="input py-1.5 text-sm w-24" value={row.views || ''}
                                        placeholder={t('mediaKit.viewsPlaceholder')}
                                        onChange={(e) => set('sample_content', form.sample_content.map(
                                            r => (r.id === row.id ? { ...r, views: e.target.value } : r)))} />
                                    <button type="button" onClick={() => set('sample_content', form.sample_content.filter(r => r.id !== row.id))}
                                        className="text-red-300 hover:text-red-600 px-1">×</button>
                                </div>
                            ))}
                        </div>
                        <button type="button"
                            onClick={() => set('sample_content', [...form.sample_content, { id: uid(), type: 'link', url: '', platform: '', views: '' }])}
                            className="btn-secondary text-xs mt-3 py-1.5 px-3">+ {t('mediaKit.addSampleLink')}</button>
                    </Fieldset>

                    {/* ── Prices, edited right here so the page is one job.
                        Saved by this sheet's footer button too. ── */}
                    <Fieldset title={t('rateCard.title')} hint={t('mediaKit.rateCardHint')}>
                        <RateCardEditor card={data?.rate_card}
                            registerSave={(fn) => { saveRates.current = fn; }} />
                    </Fieldset>

                    {/* ── Private share link ── */}
                    <Fieldset title={t('mediaKit.shareTitle')} hint={t('mediaKit.shareHint')}>
                        <div className="flex gap-2">
                            <input readOnly className="input py-2 text-xs flex-1 bg-slate-50 text-slate-500"
                                value={privateLink} onFocus={(e) => e.target.select()} />
                            <button type="button" onClick={copyLink} disabled={!privateLink}
                                className="btn-secondary px-3 text-xs whitespace-nowrap disabled:opacity-40">
                                {copied ? t('mediaKit.copied') : t('mediaKit.copy')}
                            </button>
                        </div>
                        <button type="button" onClick={rotateToken}
                            className="text-[11px] font-semibold text-red-500 hover:text-red-700 mt-2">
                            {t('mediaKit.rotateToken')}
                        </button>
                        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{t('mediaKit.rotateTokenHint')}</p>
                    </Fieldset>

                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{error}</p>
                    )}
                </div>

                <div className="flex items-center gap-3 px-5 py-4 bg-white border-t border-slate-100 flex-shrink-0">
                    <button type="button" onClick={onClose} className="btn-secondary flex-1 py-2.5 text-sm">
                        {t('campaigns.cancel')}
                    </button>
                    <button type="button" disabled={saving} onClick={() => save(false)}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition disabled:opacity-50">
                        {t('mediaKit.saveDraft')}
                    </button>
                    <button type="button" disabled={saving} onClick={() => save(true)}
                        className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2">
                        {saving && <span className="spinner-sm" />}
                        {t('mediaKit.publish')}
                    </button>
                </div>
            </div>

            {/* Modal offsets its z-index by nesting depth, so the crop sheet
                lands above this one rather than under it. */}
            <ImageCropModal
                file={coverFile}
                aspect={MEDIA_KIT_COVER_ASPECT}
                title={t('mediaKit.coverCropTitle')}
                busy={uploading}
                cancelLabel={t('campaigns.cancel')}
                onCancel={() => setCoverFile(null)}
                onCropped={uploadCover}
            />
        </div>
    );
}
